import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/common/Page";
import { money, InvBadge, TRANSITIONS, PAYMENT_METHODS, openInvoicePdf } from "@/pages/invoices/invoiceConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, FileDown, Ban, CreditCard } from "lucide-react";

const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 focus-visible:ring-cyan-500";

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ amount: "", method: "Bank Transfer", reference: "", notes: "", date: new Date().toISOString().slice(0, 10) });

  const { data: inv } = useQuery({ queryKey: ["invoice", id], queryFn: async () => (await api.get(`/invoices/${id}`)).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["invoice", id] }); qc.invalidateQueries({ queryKey: ["invoices"] }); qc.invalidateQueries({ queryKey: ["inv-dashboard"] }); };

  const setStatus = useMutation({
    mutationFn: async (status) => (await api.post(`/invoices/${id}/status`, { status })).data,
    onSuccess: () => { refresh(); toast.success("Status updated"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const voidInv = useMutation({
    mutationFn: async () => (await api.post(`/invoices/${id}/void`)).data,
    onSuccess: () => { refresh(); toast.success("Invoice voided"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const addPay = useMutation({
    mutationFn: async () => (await api.post(`/invoices/${id}/payments`, { ...pay, amount: Number(pay.amount) })).data,
    onSuccess: () => { refresh(); setPayOpen(false); setPay({ ...pay, amount: "", reference: "", notes: "" }); toast.success("Payment recorded"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (!inv) return null;
  const next = TRANSITIONS[inv.status] || [];

  return (
    <div data-testid="invoice-detail">
      <button onClick={() => navigate("/invoices")} className="mb-3 flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-300" data-testid="back-to-invoices"><ArrowLeft className="h-4 w-4" /> Invoices</button>
      <PageHeader title={inv.number} subtitle={`${inv.bill_to?.legal_name || ""}${inv.gst_registered ? " · TAX INVOICE" : " · INVOICE"}`}>
        <InvBadge inv={inv} testId="detail-status-badge" />
        <Button variant="outline" onClick={() => openInvoicePdf(id)} className="border-[#2D3F62] bg-transparent text-slate-200 hover:bg-[#17233B]" data-testid="invoice-pdf-btn"><FileDown className="mr-1.5 h-4 w-4" /> PDF</Button>
        {next.filter((s) => s !== "Void").map((s) => (
          <Button key={s} onClick={() => setStatus.mutate(s)} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid={`status-${s.replace(/ /g, "-").toLowerCase()}-btn`}>{s === "Draft" ? "Revert to Draft" : s}</Button>
        ))}
        {inv.status !== "Draft" && inv.status !== "Void" && inv.balance_cents > 0 && (
          <Button onClick={() => setPayOpen(true)} className="bg-gradient-to-r from-emerald-500 to-cyan-500 font-semibold text-slate-900" data-testid="record-payment-btn"><CreditCard className="mr-1.5 h-4 w-4" /> Record Payment</Button>
        )}
        {next.includes("Void") && <Button variant="outline" onClick={() => voidInv.mutate()} className="border-rose-500/30 bg-transparent text-rose-300 hover:bg-rose-500/10" data-testid="void-btn"><Ban className="mr-1.5 h-4 w-4" /> Void</Button>}
      </PageHeader>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4">
          <SectionTitle className="mb-3">Line items</SectionTitle>
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="py-2">Description</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Rate</th><th className="py-2 text-right">GST</th><th className="py-2 text-right">Amount</th></tr></thead>
            <tbody className="divide-y divide-[#1E2D4A]">
              {inv.line_items.map((it, i) => (
                <tr key={i}><td className="py-2 text-slate-200">{it.description}<span className="block text-[11px] text-slate-500">{it.unit} · {it.gst_mode}</span></td><td className="py-2 text-right text-slate-400">{it.qty}</td><td className="py-2 text-right text-slate-400">{money(it.unit_price_cents)}</td><td className="py-2 text-right text-slate-400">{money(it.gst_cents)}</td><td className="py-2 text-right text-slate-200">{money(it.total_cents)}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="mt-4 ml-auto w-full max-w-xs space-y-1.5 text-sm">
            <div className="flex justify-between text-slate-400"><span>Subtotal</span><span className="text-slate-200">{money(inv.subtotal_cents)}</span></div>
            <div className="flex justify-between text-slate-400"><span>GST ({inv.gst_rate}%)</span><span className="text-slate-200">{money(inv.gst_cents)}</span></div>
            <div className="flex justify-between border-t border-[#1E2D4A] pt-1.5 font-semibold text-slate-100"><span>Total</span><span>{money(inv.total_cents)}</span></div>
            <div className="flex justify-between text-slate-400"><span>Paid</span><span>{money(inv.amount_paid_cents)}</span></div>
            <div className="flex justify-between font-semibold text-cyan-300"><span>Balance Due</span><span data-testid="balance-due">{money(inv.balance_cents)}</span></div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4">
            <SectionTitle className="mb-2">Bill To</SectionTitle>
            <p className="text-sm text-slate-200">{inv.bill_to?.legal_name}</p>
            <p className="text-xs text-slate-500">{inv.bill_to?.abn ? `ABN ${inv.bill_to.abn}` : ""}<br />{inv.bill_to?.address}<br />{inv.bill_to?.email}</p>
            <p className="mt-2 text-xs text-slate-500">Issue {inv.issue_date || "—"} · Due {inv.due_date || "—"}</p>
            {(inv.period_start || inv.period_end) && (
              <p className="mt-1 text-xs text-slate-400" data-testid="invoice-service-period">
                <span className="text-slate-500">Service period:</span> {inv.period_start || "—"} to {inv.period_end || "—"}
              </p>
            )}
            {inv.commercial_snapshot && (
              <p className="mt-1 text-xs text-slate-400" data-testid="invoice-commercial-snapshot">
                <span className="text-slate-500">Commercial package:</span>{" "}
                <span className="font-mono text-cyan-300">{inv.commercial_snapshot.package_code}</span>
                {inv.commercial_snapshot.package_name ? ` · ${inv.commercial_snapshot.package_name}` : ""}
                {inv.commercial_snapshot.billing_cycle ? ` · ${inv.commercial_snapshot.billing_cycle}` : ""}
              </p>
            )}
          </div>
          <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4">
            <SectionTitle className="mb-2">Payments</SectionTitle>
            <div className="space-y-1.5" data-testid="payments-list">
              {(inv.payments || []).length === 0 && <p className="text-xs text-slate-500">No payments recorded.</p>}
              {(inv.payments || []).map((p) => (
                <div key={p.id} className="flex justify-between rounded-md border border-[#1E2D4A] bg-[#0B1120] px-2.5 py-1.5 text-xs"><span className="text-slate-300">{money(p.amount_cents)} · {p.method}</span><span className="text-slate-500">{p.date}</span></div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4">
            <SectionTitle className="mb-2">Activity</SectionTitle>
            <div className="space-y-1 text-[11px] text-slate-500" data-testid="invoice-history">
              {(inv.history || []).slice().reverse().map((h, i) => <div key={i}>{h.event} · {h.by} · {new Date(h.at).toLocaleString()}</div>)}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100 sm:max-w-md" data-testid="payment-dialog">
          <DialogHeader><DialogTitle>Record Payment · Balance {money(inv.balance_cents)}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-400">Amount ($)</Label><Input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="payment-amount-input" /></div>
              <div><Label className="text-xs text-slate-400">Date</Label><Input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="payment-date-input" /></div>
            </div>
            <div><Label className="text-xs text-slate-400">Method</Label>
              <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}><SelectTrigger className={`mt-1 ${inputCls}`} data-testid="payment-method-select"><SelectValue /></SelectTrigger><SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-xs text-slate-400">Reference</Label><Input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="payment-reference-input" /></div>
          </div>
          <DialogFooter><Button onClick={() => addPay.mutate()} disabled={addPay.isPending || !pay.amount} className="bg-gradient-to-r from-emerald-500 to-cyan-500 font-semibold text-slate-900" data-testid="payment-confirm">Record</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
